import { useMemo } from 'react'

import {
  type OfferV11,
  type PimProductV11,
  PimProductV1StockStatusEnum,
} from '@redteclab/api/clients/bully'

import { productAccessorIsSellerPromotionAvailable } from '../../../../product'
import { productAccessorGetActiveFlags } from '../../../model/productAccessor/productAccessorGetActiveFlags'
import { productAccessorGetOfferPromotionType } from '../../../model/productAccessor/productAccessorGetOfferPromotionType'
import {
  productAccessorGetCurrentTimeIsInRedpointsTimeframe,
  productAccessorGetRedPointsForOffer,
  productAccessorGetRedPointsMultiplier,
} from '../../../model/productAccessor/productAccessorGetRedPointsRedPointsAmountWithMultiplier'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorOfferHasPromotionSetOfferFlag } from '../../../model/productAccessor/productAccessorOfferHasPromotionSetOfferFlag'
import { productAccessorOfferNowProductGroupIsSameDayNextDay } from '../../../model/productAccessor/productAccessorOfferNowProductGroupIsSameDayNextDay'

import { type ProductHeadLabel } from './ProductHeadLabelsTypes'

const getNowLabel = (
  product: PimProductV11,
  offer?: OfferV11,
): ProductHeadLabel | undefined => {
  if (
    !offer ||
    productAccessorIsPrescriptionProduct(product) ||
    !productAccessorOfferNowProductGroupIsSameDayNextDay(offer)
  ) {
    return undefined
  }

  return {
    type: 'now',
  }
}

const TWO = 2

const getRedPointsMultiplierLabel = (
  offer?: OfferV11,
): ProductHeadLabel | undefined => {
  if (!offer) {
    return undefined
  }

  const redPoints = productAccessorGetRedPointsForOffer(offer)

  if (
    !redPoints ||
    !productAccessorGetCurrentTimeIsInRedpointsTimeframe(redPoints)
  ) {
    return undefined
  }

  const multiplier = productAccessorGetRedPointsMultiplier(redPoints)
  if (multiplier < TWO) {
    return undefined
  }

  return {
    labelText: 'multiplier',
    redPointsMultiplierSpecific: {
      multiplier,
    },
    type: 'redPointsMultiplier',
  }
}

const labelOrder = [
  'prescription_medicine',
  'redPointsMultiplier',
  'promotion',
  'now',
  'outOfSale',
]

// Collects active flags transforms to labels. Type is used from key of the flag.
const getLabelsFromFlags = (product: PimProductV11): ProductHeadLabel[] => {
  const activeFlags = productAccessorGetActiveFlags(product)

  return (
    Object.entries(activeFlags)
      // we need to filter the subscription flag, because subscriptions are being dropped off from the shop
      .filter(([flag]) => flag !== 'product_subscription')
      .toSorted(([flagA], [flagB]) => {
        const indexA = labelOrder.indexOf(flagA)
        const indexB = labelOrder.indexOf(flagB)

        if (indexA === indexB) {
          return 0
        }

        return labelOrder.indexOf(flagA) > labelOrder.indexOf(flagB) ? -1 : 1
      })
      .map(([key, flag]) => {
        return {
          labelText: flag.label,
          type: key as ProductHeadLabel['type'],
        }
      })
  )
}

const getPromotionSetLabel = (
  offer?: OfferV11,
): ProductHeadLabel | undefined => {
  if (!offer || !productAccessorOfferHasPromotionSetOfferFlag(offer)) {
    return undefined
  }

  return {
    type: 'promotionSet',
  }
}

/**
 * if current offer has any promotion related to it
 */
const getPromotionLabel = (offer?: OfferV11): ProductHeadLabel | undefined => {
  if (!offer) {
    return undefined
  }

  const promotionType = productAccessorGetOfferPromotionType(offer)

  if (!promotionType) {
    return undefined
  }

  return {
    promotionSpecific: {
      promotionType,
    },
    type: 'promotion',
  }
}

const getOutOfSaleLabel = (
  product: PimProductV11,
): ProductHeadLabel | undefined => {
  if (product.stock_status === PimProductV1StockStatusEnum.OutOfSale) {
    return {
      type: 'outOfSale',
    }
  }

  return undefined
}

const getSellerPromotionLabel = (
  offer?: OfferV11,
): ProductHeadLabel | undefined => {
  if (!productAccessorIsSellerPromotionAvailable(offer)) {
    return undefined
  }

  return {
    type: 'sellerPromotion',
  }
}

const removeDuplicateTextLabels = (
  labels: ProductHeadLabel[],
): ProductHeadLabel[] => {
  const existing: Record<string, ProductHeadLabel | undefined> = {}

  return labels.filter((label) => {
    if (!label.labelText) {
      return true
    }
    // same label was met before
    if (existing[label.labelText]) {
      return false
    }

    // add label to existing by text, so we can filter next if it will have the same one.
    existing[label.labelText] = label

    return true
  })
}

export const useProductHeadLabels = ({
  offer,
  product,
}: {
  offer?: OfferV11
  product: PimProductV11
}): ProductHeadLabel[] => {
  return useMemo((): ProductHeadLabel[] => {
    const labels = getLabelsFromFlags(product)

    for (const label of [
      getSellerPromotionLabel(offer),
      getRedPointsMultiplierLabel(offer),
      getPromotionLabel(offer),
      getOutOfSaleLabel(product),
      getPromotionSetLabel(offer),
      getNowLabel(product, offer),
    ]) {
      if (label) {
        labels.push(label)
      }
    }

    /**
     * https://jira.shop-apotheke.com/browse/WSAWA-3871
     * edgecase when labels are same for different flags, filter to prevent double display.
     * Known case is FR
     * "medicament": {
     *    "active": true,
     *    "label": "Médicament" // <---
     * },
     * "pharma_obl": {
     *   "active": true,
     *   "label": "Médicament" // <---
     * }
     */
    return removeDuplicateTextLabels(labels)
  }, [offer, product])
}
